import {
  reduceOutageSummary,
  serviceHasOutage,
  serviceHasLocalProblem,
} from './outageHelpers';

// Ported business rules from MyAccountWeb-FE
// src/app/directives/eid-service-hero/EidServiceHeroController.js.

// Legacy CONFIG.elevatedIssueDetection.minimumScanTime.
export const MINIMUM_SCAN_TIME = 5000;

// Legacy URL.SERVICES.MODEM_RESTART_HS.
export const MODEM_RESTART_HS = 'https://www.xfinity.com/support/repair/internet';

// Legacy SMS enroll fallback link used by the non-LOB outage path.
const SMS_ENROLL_LINK = 'https://www.xfinity.com/support/enroll-sms/?continue=https%3A%2F%2Fwww.xfinity.com%2Fsupport%2Fstatus';

// Maps consolidated-LOB impact style to an overlay severity.
const outageSeverities = {
  'high impact': 'outage',
  'medium impact': 'medium',
  default: 'notice',
};

// Maps a directive outageType to the consolidated-LOB line-of-business code.
const lobMap = {
  tv: 'Video',
  internet: 'HSD',
  voice: 'Telephony',
  flex: 'HSD',
};

// Returns the CWA static asset root for the current environment.
export const getCwaRoot = () => {
  const env = window.MAW && window.MAW.env;
  return env === 'staging'
    ? 'https://staging-static.cimcontent.net/common-web-assets/'
    : 'https://static.cimcontent.net/common-web-assets/';
};

// Builds the devices EID hero image map (same SVGs as MyAccountWeb-FE).
export const getDevicesImages = () => {
  const root = getCwaRoot();
  return {
    header: `${root}svg/eid/devices_null.svg`,
    outage: `${root}svg/eid/devices_outage.svg`,
    issue: `${root}svg/eid/devices_issue.svg`,
    notice: `${root}svg/eid/devices_notice.svg`,
    medium: `${root}svg/eid/devices_medium.svg`,
    ok: `${root}svg/eid/devices_noissue.svg`,
  };
};

// Applies SMS-enroll link details onto a problem descriptor.
const applySmsEnroll = (problem, web) => {
  const { link, linkText, linkTarget } = web || {};
  if (link && linkText) {
    return {
      ...problem, url: link, buttonLabel: linkText, target: linkTarget,
    };
  }
  return problem;
};

// Resolves outage + device status via the non-LOB (legacy) outage endpoint.
export const resolveOutage = (
  outageData = {},
  deviceStatus = {},
  { outageType, useSsmDeviceStatus = true } = {},
  { enableSmsEnrollOutageNBA = false } = {},
) => {
  const {
    outage, summary, customerMessage = {}, smsEnroll,
  } = outageData;
  const { outage: { message, outageTimeFrame, outageType: msgType } = {} } = customerMessage;
  const isCurrent = outageTimeFrame === 'CURRENTOUTAGE';
  const isInfo = msgType && msgType.toLowerCase() === 'informational';
  const isOverride = msgType && msgType.toLowerCase() === 'override';

  let lob = (outageType || '').toLowerCase();
  if (lob === 'tv') lob = 'video';

  let hasOutage;
  if (outageType) {
    const outageSummary = outage && reduceOutageSummary(summary);
    hasOutage = Boolean(isCurrent && outageSummary && serviceHasOutage(lob, outageSummary));
  } else {
    hasOutage = Boolean(isCurrent && outage);
  }

  const noLink = serviceHasLocalProblem(lob || null, deviceStatus, useSsmDeviceStatus);
  let problem = null;
  let infoOutage = false;
  if (hasOutage) {
    infoOutage = Boolean(isInfo || isOverride);
    problem = { message };
    if (enableSmsEnrollOutageNBA && smsEnroll) {
      problem = applySmsEnroll(problem, smsEnroll.web);
    } else if (!infoOutage) {
      problem = {
        ...problem, url: SMS_ENROLL_LINK, buttonLabel: 'Text me with Updates',
      };
    }
  }
  return {
    hasOutage, noLink, problem, infoOutage, outageSeverity: null,
  };
};

// Determines whether the consolidated outage info list reports a current outage.
const hasCurrentLob = (outageInfoResponseList = []) => {
  const [isCurrent] = outageInfoResponseList.reduce((acc, { outages, services = [] }) => {
    const [current, svcs] = acc;
    if ((outages || '').toLowerCase() !== 'current') {
      return [current, svcs];
    }
    services.forEach((item) => {
      if (!svcs.some(existing => JSON.stringify(existing) === JSON.stringify(item))) {
        svcs.push(item);
      }
    });
    return [true, svcs];
  }, [!outageInfoResponseList.length, []]);
  return isCurrent;
};

// Finds the matching LOB outage block for a given outageType/serviceType.
const findLobOutage = (lob, outageType, serviceType) => {
  if (!lobMap[outageType.toLowerCase()]) return {};
  return lob.find(line => (
    line.services.find(service => service.toLowerCase() === serviceType.toLowerCase())
    && lobMap[outageType.toLowerCase()].indexOf(line.lob) !== -1
  )) || {};
};

// Resolves outage + device status via the consolidated-LOB outage endpoint.
export const resolveOutageLob = (
  consolidatedData = {},
  deviceStatus = {},
  { outageType, serviceType, useSsmDeviceStatus = true } = {},
) => {
  const {
    lob = [],
    customerMessage: {
      optInEnroll, messages, outageType: msgType, impactStyle, planned,
    } = {},
    smsEnroll,
    outageInfoResponseList = [],
  } = consolidatedData;
  const isCurrent = hasCurrentLob(outageInfoResponseList);
  const isInfo = msgType && msgType.toLowerCase() === 'informational';
  const isOverride = msgType && msgType.toLowerCase() === 'override';

  let lobOutage = {};
  let hasOutage;
  if (outageType) {
    lobOutage = findLobOutage(lob, outageType, serviceType);
    hasOutage = Boolean(isCurrent && lobOutage && lobOutage.impactStyle);
  } else {
    hasOutage = Boolean(isCurrent && impactStyle);
  }

  const noLink = serviceHasLocalProblem(outageType || null, deviceStatus, useSsmDeviceStatus);
  let problem = hasOutage ? {
    headline: (lobOutage.messages || messages).shortDescription,
    message: (lobOutage.messages || messages).detailedDescription,
  } : null;
  let outageSeverity = null;
  let infoOutage = false;

  const activeImpact = lobOutage.impactStyle || impactStyle;
  if (
    hasOutage
    && (problem.headline || problem.message)
    && activeImpact.toLowerCase() !== 'resolved'
  ) {
    outageSeverity = outageSeverities[impactStyle.toLowerCase()] || outageSeverities.default;
    infoOutage = Boolean(isInfo || isOverride);
    if (optInEnroll === 'enrollAllowed') {
      problem = applySmsEnroll(problem, smsEnroll && smsEnroll.web);
    } else {
      problem = {
        ...problem, url: null, buttonLabel: null, target: null,
      };
    }
  } else {
    hasOutage = false;
    outageSeverity = null;
    problem = null;
  }
  return {
    hasOutage, noLink, problem, outageSeverity, infoOutage, planned,
  };
};

// Normalizes a parsed ITG recommendation into a problem descriptor.
export const resolveItg = (itgData) => {
  if (!itgData) return null;
  const {
    name, description, link, linkText, docId, recommendationsName,
  } = itgData;
  return {
    problem: {
      headline: name, message: description, url: link, buttonLabel: linkText,
    },
    itgProblem: docId,
    recommendationsName,
  };
};

// Computes overlay/action flags (legacy setFlags) from resolved outage/ITG state.
export const computeFlags = ({
  hasOutage,
  outageSeverity,
  problem: incomingProblem,
  noLink,
  scanning,
  scanned,
  repairHref = MODEM_RESTART_HS,
  deviceTypeName = 'Modem',
  enableOutageLob,
}) => {
  let problem = incomingProblem;
  if (noLink && !problem && scanned && deviceTypeName) {
    problem = {
      headline: 'We\'re having trouble finding a signal',
      message: 'Make sure your device is connected to a power source. '
        + 'If you\'re still experiencing issues, let\'s restart your device.',
      url: repairHref,
      buttonLabel: `Restart ${deviceTypeName}`,
    };
  }
  const outageOverlay = enableOutageLob
    ? (outageSeverity === 'outage' && !scanning)
    : Boolean(hasOutage && !scanning);
  const issueOverlay = Boolean(!hasOutage && (problem || noLink) && !scanning);
  const noticeOverlay = enableOutageLob ? outageSeverity === 'notice' : false;
  const mediumOverlay = enableOutageLob ? outageSeverity === 'medium' : false;
  const overlay = outageOverlay || issueOverlay || noticeOverlay || mediumOverlay;
  const allClear = Boolean(!overlay && !problem && !noLink && scanned && !scanning);
  const runDiagnostics = Boolean(!hasOutage && !problem && !scanning && !scanned);
  const showActions = Boolean(!scanning && (
    runDiagnostics
    || outageOverlay
    || (allClear && repairHref)
    || problem
  ));
  const issueDetected = Boolean(hasOutage || noLink || problem);
  return {
    problem,
    outageOverlay,
    issueOverlay,
    noticeOverlay,
    mediumOverlay,
    allClear,
    runDiagnostics,
    showActions,
    issueDetected,
  };
};

// Selects which hero image key to render based on computed flags.
export const getImageKey = ({
  outageOverlay, issueOverlay, noticeOverlay, mediumOverlay, allClear,
}) => {
  if (outageOverlay) return 'outage';
  if (issueOverlay) return 'issue';
  if (noticeOverlay) return 'notice';
  if (mediumOverlay) return 'medium';
  if (allClear) return 'ok';
  return 'header';
};
